import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
const navLinks = [{
  label: 'Services',
  route: '/services'
}, {
  label: 'Why AccuTemp',
  route: '/why-accutemp'
}, {
  label: 'Case studies',
  route: '/case-studies'
}, {
  label: 'Resources',
  route: '/knowledge-hub'
}];
const NavIndicator = ({
  active,
  isMobile = false
}: {
  active: boolean;
  isMobile?: boolean;
}) => {
  if (!active) return null;
  return <motion.span initial={{
    opacity: 0,
    scaleY: 0
  }} animate={{
    opacity: 1,
    scaleY: 1
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} className={`inline-block origin-bottom ${isMobile ? 'w-2 h-5' : 'w-1.5 h-3.5'}`} data-id="element-200">
      <svg viewBox="0 0 6 14" className="w-full h-full text-accent overflow-visible" data-id="element-201">
        <line x1="5" y1="14" x2="1" y2="0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-202" />
      </svg>
    </motion.span>;
};
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  const isHome = location.pathname === '/';
  const getHref = (link: (typeof navLinks)[0]) => {
    if (link.route) return link.route;
    return isHome ? link.anchor : `/${link.anchor}`;
  };
  const handleLinkClick = () => {
    setMenuOpen(false);
  };
  const textStyle = isHome && !scrolled ? 'text-white/90' : 'text-primary-dark/80';
  const hoverStyle = isHome && !scrolled ? 'hover:text-white' : 'hover:text-accent';
  return <>
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#E8F0F8] backdrop-blur-sm py-4 shadow-lg' : isHome ? 'bg-transparent py-6' : 'bg-[#E8F0F8] py-6'}`} data-id="element-203">
        <div className="container mx-auto px-6 flex justify-between items-center" data-id="element-204">
          {/* Logo */}
          <Link to="/" className="flex items-center relative z-50" onClick={handleLinkClick} data-id="element-205">
            <img src={isHome && !scrolled ? 'https://cdn.magicpatterns.com/uploads/m22cd52VGFAbga9BNLUyHJ/accutemp-logo-white.svg' : 'https://cdn.magicpatterns.com/uploads/gniRXuhgWi11P326tXmzet/accutemp-logo-colour.svg'} alt="AccuTemp Air Conditioning — commercial HVAC specialists in Coventry" className="h-8 md:h-10 w-auto" data-id="element-206" />
          </Link>

          {/* Desktop nav — visible lg+ */}
          <div className={`hidden lg:flex items-center gap-8 text-sm font-medium transition-colors ${textStyle}`} data-id="element-207">
            {navLinks.map(link => {
            const active = link.route ? location.pathname.startsWith(link.route) : isHome;
            return link.route ? <Link key={link.label} to={link.route} className={`group transition-colors flex items-center gap-1.5 ${hoverStyle} ${active ? isHome && !scrolled ? 'text-white' : 'text-primary-dark' : ''}`} data-id="element-208">
                  <NavIndicator active={active} data-id="element-209" />
                  <span data-id="element-210">{link.label}</span>
                </Link> : <a key={link.label} href={getHref(link)} className={`group transition-colors flex items-center gap-1.5 ${hoverStyle} ${active ? isHome && !scrolled ? 'text-white' : 'text-primary-dark' : ''}`} data-id="element-211">
                  <NavIndicator active={active} data-id="element-212" />
                  <span data-id="element-213">{link.label}</span>
                </a>;
          })}
            <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors ml-2" data-id="element-214">
              Contact us
            </Link>
          </div>

          {/* Mobile burger — visible below lg */}
          <button onClick={() => setMenuOpen(!menuOpen)} className={`lg:hidden relative z-50 transition-colors ${isHome && !scrolled ? 'text-white hover:text-white/80' : menuOpen ? 'text-white hover:text-white/80' : 'text-primary-dark hover:text-primary-dark/70'}`} aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} data-id="element-215">
            {menuOpen ? <X size={28} data-id="element-216" /> : <Menu size={28} data-id="element-217" />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile slide-from-right menu */}
      <AnimatePresence data-id="element-218">
        {menuOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden" onClick={handleLinkClick} data-id="element-219" />

            {/* Panel */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed top-0 right-0 bottom-0 z-40 w-full max-w-sm bg-primary-dark shadow-2xl lg:hidden" data-id="element-220">
              <div className="flex flex-col h-full pt-24 pb-12 px-8" data-id="element-221">
                {/* Nav links */}
                <nav className="flex flex-col gap-1 flex-1" data-id="element-222">
                  {navLinks.map((link, index) => {
                const active = link.route ? location.pathname.startsWith(link.route) : isHome;
                return <motion.div key={link.label} initial={{
                  opacity: 0,
                  x: 30
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: 20
                }} transition={{
                  duration: 0.3,
                  delay: index * 0.06
                }} data-id="element-223">
                        {link.route ? <Link to={link.route} onClick={handleLinkClick} className={`group flex items-center gap-3 text-2xl font-serif py-4 hover:text-accent transition-colors border-b border-white/5 ${active ? 'text-accent' : 'text-white'}`} data-id="element-224">
                            <NavIndicator active={active} isMobile data-id="element-225" />
                            <span data-id="element-226">{link.label}</span>
                          </Link> : <a href={getHref(link)} onClick={handleLinkClick} className={`group flex items-center gap-3 text-2xl font-serif py-4 hover:text-accent transition-colors border-b border-white/5 ${active ? 'text-accent' : 'text-white'}`} data-id="element-227">
                            <NavIndicator active={active} isMobile data-id="element-228" />
                            <span data-id="element-229">{link.label}</span>
                          </a>}
                      </motion.div>;
              })}
                </nav>

                {/* Contact CTA + info at bottom */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.3
            }} className="space-y-6" data-id="element-230">
                  <Link to="/contact" onClick={handleLinkClick} className="block w-full bg-accent hover:bg-accent/90 text-white text-center px-6 py-4 rounded-full font-medium transition-colors" data-id="element-231">
                    Contact us
                  </Link>
                  <div className="flex items-center gap-4 text-white/40 text-sm" data-id="element-232">
                    <a href="https://linkedin.com/company/accutemp" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-233">
                      LinkedIn
                    </a>
                    <span className="w-1 h-1 rounded-full bg-white/30" data-id="element-234"></span>
                    <span data-id="element-235">Coventry, CV6 4DD</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}